import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bed } from "@phosphor-icons/react";
import api, { mediaUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { pick } from "@/lib/languages";
import { formatNumber } from "@/lib/format";
import { homeL } from "@/i18n/home";
import { hotelsL } from "@/i18n/stays";
import { ratingWord } from "@/lib/ratings";
import { dsL } from "@/i18n/ds";
import ResultCard from "@/components/ds/ResultCard";
import Price from "@/components/ds/Price";
import { Skeleton, SkeletonGroup, SkeletonLine } from "@/components/ds/Skeleton";
import HomeSection from "./HomeSection";
import { Stars } from "@/components/HotelCard";

// The three groups a traveller chooses between (backend modules/catalogue/
// stays.py STAY_TYPE_GROUPS), for a row that does not say which it is.
const GROUP = {
  hotel: "hotel", resort: "hotel", lodge: "hotel", hostel: "hotel", motel: "hotel", inn: "hotel",
  aparthotel: "hotel", riad: "hotel", guesthouse: "hotel",
  apartment: "apartment", flat: "apartment", studio: "apartment", room: "apartment",
  house: "house", villa: "house", vacation_home: "house", chalet: "house", bungalow: "house", cottage: "house",
};
// Grid photos: about 290 px wide from 1024 px, half the row on a tablet,
// 80% of a phone (the row scrolls sideways there).
const PHOTO_SIZES = "(min-width: 1024px) 290px, (min-width: 640px) 45vw, 80vw";
// Phones scroll through all of them. The grids show full rows only: up to
// two rows of two on a tablet, one or two rows of four on a wide screen, so
// a fifth stay never sits alone on a row.
const SHOW = 8;
const fullRows = (count, columns, rows) => Math.min(count, columns * rows) - (Math.min(count, columns * rows) % columns || 0);
function visibleFrom(count) {
  const wide = fullRows(count, 4, 2) || count;
  const tablet = fullRows(count, 2, 2) || count;
  return (i) => (i >= wide ? "lg:hidden " : "") + (i >= tablet ? "sm:max-lg:hidden" : "");
}

/**
 * Featured stays: the listings' own photos, the name in full, the place and
 * kind, a score only when real reviews are behind it, and a price that says
 * what it is. Without dates that is "from X / night" (the lowest nightly
 * rate); with a quote from the server it is the total first. The band is
 * left out when there is nothing to show or the request fails: Home is not
 * the place for an empty state.
 */
export default function FeaturedStays() {
  const { lang, t } = useSettings();
  const l = pick(homeL, lang).featured;
  const [stays, setStays] = useState(null);

  useEffect(() => {
    let live = true;
    api.get("/hotels", { params: { featured: true, limit: SHOW } })
      .then((r) => { if (live) setStays(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (live) setStays([]); });
    return () => { live = false; };
  }, []);

  if (stays && !stays.length) return null;
  const seeAll = (
    <Link to="/hotels" data-testid="featured-see-all"
      className="inline-flex min-h-11 items-center text-label-lg font-semibold text-touki-black underline decoration-touki-field underline-offset-4 transition-colors duration-fast hover:decoration-touki-black">
      {l.seeAll}
    </Link>
  );
  return (
    <HomeSection id="featured" title={t("sections.weekend")} note={l.note} action={seeAll} data-testid="home-featured">
      {stays === null ? (
        <SkeletonGroup label={l.loading} data-testid="featured-status" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <StaySkeleton key={i} className={i > 0 ? "max-sm:hidden" : ""} />)}
        </SkeletonGroup>
      ) : (
        <>
          <p role="status" className="sr-only" data-testid="featured-status" />
          <ul data-testid="featured-grid"
            className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-4 px-4 pb-1 scrollbar-none sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-4">
            {stays.slice(0, SHOW).map((hotel, i, shown) => (
              <li key={hotel.id} className={`w-[80%] shrink-0 snap-start sm:w-auto ${visibleFrom(shown.length)(i)}`}>
                <StayCard hotel={hotel} l={l} lang={lang} />
              </li>
            ))}
          </ul>
        </>
      )}
    </HomeSection>
  );
}

/** The card's own shape while it loads (photo, name, place, kind, price), so nothing moves when it arrives. */
function StaySkeleton({ className }) {
  return (
    <div aria-hidden="true" className={`flex flex-col gap-3 rounded-lg border border-touki bg-white p-3 ${className}`}>
      <Skeleton className="aspect-[4/3] w-full rounded-sm" />
      <div className="flex flex-col gap-2">
        <SkeletonLine width="70%" size="lg" />
        <SkeletonLine width="45%" />
        <SkeletonLine width="35%" />
        <SkeletonLine width="50%" size="lg" className="mt-3" />
      </div>
    </div>
  );
}

function StayCard({ hotel, l, lang }) {
  const place = hotel.area || [...new Set([hotel.neighborhood, hotel.city].filter(Boolean))].join(", ");
  const kind = l.types[hotel.stay_type || GROUP[hotel.property_type]];
  const stars = Math.round(Number(hotel.stars)) || 0;
  return (
    <ResultCard data-testid={`hotel-card-${hotel.id}`} className="h-full sm:flex-col sm:gap-3 sm:p-3">
      <ResultCard.Media src={hotel.images?.[0] ? mediaUrl(hotel.images[0]) : null} icon={Bed} sizes={PHOTO_SIZES}
        className="aspect-[4/3] sm:aspect-[4/3] sm:h-auto sm:w-full lg:h-auto lg:w-full max-sm:aspect-[4/3]" />
      <ResultCard.Body className="gap-1">
        <ResultCard.Title as="h3" className="text-heading-sm">
          <ResultCard.Link as={Link} to={`/hotels/${hotel.id}`}>{hotel.name}</ResultCard.Link>
        </ResultCard.Title>
        {place ? <ResultCard.Meta>{place}</ResultCard.Meta> : null}
        {kind || stars ? (
          <p className="flex flex-wrap items-center gap-x-2 text-label-md text-touki-muted">
            {kind ? <span>{kind}</span> : null}
            <Stars count={stars} l={l} size={12} />
          </p>
        ) : null}
        <Score hotel={hotel} l={l} lang={lang} />
        <div className="mt-auto pt-2">
          <StayPrice hotel={hotel} l={l} lang={lang} />
        </div>
      </ResultCard.Body>
    </ResultCard>
  );
}

/** "4,6/5 Très bien · 23 avis", only with reviews behind it; nothing otherwise. */
function Score({ hotel, l, lang }) {
  if (!(hotel.rating > 0 && hotel.review_count > 0)) return null;
  const score = formatNumber(hotel.rating, { lang, minimumFractionDigits: 1, maximumFractionDigits: 1 });
  // The same word as on the results and the property page (lib/ratings).
  const word = ratingWord(hotel.rating, pick(hotelsL, lang).scores);
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-label-md" data-testid="stay-score">
      <span className="sr-only">{l.scoreLabel(score, hotel.review_count)}</span>
      <span aria-hidden="true" className="inline-flex h-6 items-center rounded-sm bg-touki-dark px-1.5 font-bold text-white tabular-nums">{l.score(score)}</span>
      {word ? <span aria-hidden="true" className="font-semibold text-touki-black">{word}</span> : null}
      <span aria-hidden="true" className="text-touki-muted">{l.reviews(hotel.review_count)}</span>
    </p>
  );
}

/** The total when the server quoted dates; otherwise the nightly rate, labelled as a starting price. */
function StayPrice({ hotel, l, lang }) {
  const { formatPrice } = useSettings();
  const quote = hotel.stay_quote;
  if (quote?.total != null && quote.nights > 0) {
    return (
      <Price amount={quote.total} size="md" align="start" data-testid="stay-quote"
        context={pick(dsL, lang).count.nights(quote.nights)}
        unit={hotel.price_from != null ? { amount: hotel.price_from, per: "night" } : undefined} />
    );
  }
  if (hotel.price_from == null) return null;
  return (
    <p className="flex flex-wrap items-baseline gap-x-1.5 text-touki-black" data-testid="stay-from">
      {l.from.before ? <><span className="text-body-sm text-touki-muted">{l.from.before}</span>{" "}</> : null}
      <span dir="ltr" className="whitespace-nowrap text-price-md tabular-nums">{formatPrice(hotel.price_from)}</span>{" "}
      <span className="text-body-sm text-touki-muted">{l.from.after}</span>
    </p>
  );
}
